/**
 * Document presence: is the project open in this window also open somewhere
 * else? (OPBridge S350, operator 2026-11-03: "whenever any document is open on
 * two different computers, a warning must be displayed".)
 *
 * The answers the operator gave, which this file implements:
 *   1. a project counts as open whenever it is open, viewing included;
 *   2. EVERY window with it warns ("Also open on STREAMPC"); only the window
 *      that opened it later (`first: false`) is offered "Open read-only";
 *   3. when the other copy closes, a read-only window becomes editable on its
 *      own and says so;
 *   4. the document is a whole project, named by its doc-store id
 *      (`project-<id>`), the same on every computer;
 *   5. two windows on the SAME computer get the same treatment, named as such;
 *   6. a computer that cannot be checked (off, link down) gets a quiet note,
 *      no warning, no read-only offer.
 *
 * Wire (OPBridge PROTOCOL.md, "doc_open / doc_close ... - S350"): this window
 * says `doc_open` / `doc_close` on its `/ws/consumer` socket; OPBridge answers
 * `doc_open_elsewhere` whenever the picture changes for a document it has open.
 * A socket closing closes everything it had open, so every (re)open of the
 * socket says `doc_open` again. An older OPBridge ignores the frames, and then
 * nothing here ever shows.
 */

import { createEffect, createSignal, on, type Accessor } from 'solid-js';
import { activeProjectId } from './projectStore.ts';
import { projectReadOnly, setProjectReadOnly } from './readOnly.ts';
import { isEditMode, toggleEditMode } from './store.ts';
import { sendConsumerFrame, onConsumerSocketOpen, onDocOpenElsewhere, type DocOpenElsewhere } from '../opbridge/consumerWs.ts';
import { pushToast } from '../ui/toast.ts';

export interface DocOpener { nodeId: string; name: string; samePc: boolean; sinceMs: number }
export interface DocPresence {
  doc: string;
  /** This window had it open before every other place (a tie counts as first). */
  first: boolean;
  opens: DocOpener[];
  /** Computers of this mesh that could not be checked. */
  unchecked: string[];
}

/** The project's document name, the same on every computer (its doc-store id). */
export function projectDocKey(projectId: string | null): string | null {
  return projectId ? `project-${projectId}` : null;
}

const [presenceSig, setPresenceSig] = createSignal<DocPresence | null>(null);

/** What OPBridge last said about the open project, or null (open only here, or
 *  never answered). Reactive. */
export const docPresence: Accessor<DocPresence | null> = presenceSig;

/** "STREAMPC", "another window on this computer", "STREAMPC and Mac Studio". */
export function openersPhrase(opens: readonly DocOpener[]): string {
  const names = opens.map((o) => (o.samePc ? 'another window on this computer' : o.name || 'another computer'));
  const unique = [...new Set(names)];
  return unique.length <= 2 ? unique.join(' and ') : `${unique.slice(0, 2).join(', ')} and ${unique.length - 2} more`;
}

/** Take the open project read-only (the offer made to the window that opened it second). */
export function openReadOnly(): void {
  setProjectReadOnly(true);
  if (isEditMode()) toggleEditMode();
}

/** Edit anyway: the operator's call, always available. */
export function editAnyway(): void {
  setProjectReadOnly(false);
}

let current: string | null = null;
let installed = false;

function send(event: 'doc_open' | 'doc_close', doc: string): void {
  sendConsumerFrame({ event, doc });
}

function apply(update: DocOpenElsewhere): void {
  const mine = update.docs.find((d) => d.doc === current);
  if (!mine) return;
  const opens: DocOpener[] = mine.opens.map((o) => ({ nodeId: o.node_id, name: o.name, samePc: o.same_pc, sinceMs: o.since_ms }));
  const before = presenceSig();
  setPresenceSig({ doc: mine.doc, first: mine.first, opens, unchecked: mine.unchecked.map((u) => u.name) });
  // The other copy closed: a read-only window becomes editable on its own, and says so.
  if (opens.length === 0 && projectReadOnly()) {
    setProjectReadOnly(false);
    const who = before && before.opens.length > 0 ? openersPhrase(before.opens) : 'The other copy';
    pushToast('ok', `${who[0]!.toUpperCase()}${who.slice(1)} closed it - you can edit now.`, 6000);
  }
}

/** Wire it up. Call once at boot. */
export function installDocPresence(): void {
  if (installed) return;
  installed = true;
  onDocOpenElsewhere(apply);
  // Every socket (re)open: OPBridge forgot this window's opens when the last one closed.
  onConsumerSocketOpen(() => { if (current) send('doc_open', current); });
  createEffect(on(() => projectDocKey(activeProjectId()), (doc) => {
    if (doc === current) return;
    if (current) send('doc_close', current);
    current = doc;
    setPresenceSig(null);
    setProjectReadOnly(false);
    if (doc) send('doc_open', doc);
  }));
}
